import { X } from 'lucide-react';
import clsx from 'clsx';
import { useEffect, useCallback } from 'react';

/**
 * Modal - Reusable modal bileşeni
 * 
 * @param {boolean} isOpen - Modal açık mı
 * @param {function} onClose - Kapatma fonksiyonu
 * @param {string} title - Modal başlığı
 * @param {ReactNode} children - Modal içeriği
 * @param {string} size - Modal boyutu
 * @param {ReactNode} footer - Footer içeriği
 * @param {boolean} hideCloseButton - Kapat butonunu gizle
 * @param {boolean} closeOnOverlay - Overlay'e tıklayınca kapat
 * @param {boolean} closeOnEsc - ESC tuşuyla kapat
 * @param {boolean} fullScreenMobile - Mobilde tam ekran (varsayılan true)
 */
export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    footer,
    hideCloseButton = false,
    closeOnOverlay = true,
    closeOnEsc = true,
    fullScreenMobile = true, // Varsayılan olarak mobilde tam ekran
}) {
    // ESC tuşu ile kapatma
    const handleKeyDown = useCallback((e) => {
        if (closeOnEsc && e.key === 'Escape') {
            onClose();
        }
    }, [closeOnEsc, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    // Modal boyutları
    const sizeClasses = {
        xs: 'sm:max-w-[360px]',
        sm: 'sm:max-w-[480px]',
        md: 'sm:max-w-[560px]',
        lg: 'sm:max-w-[720px]',
        xl: 'sm:max-w-[900px]',
        '2xl': 'sm:max-w-[1080px]',
        full: 'sm:max-w-[calc(100vw-2rem)]',
    };

    const handleOverlayClick = (e) => {
        if (closeOnOverlay && e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className={clsx(
                'fixed inset-0 bg-black/40 backdrop-blur-sm z-50',
                'flex items-end sm:items-center justify-center',
                'p-0 sm:p-4'
            )}
            onClick={handleOverlayClick}
        >
            <div
                className={clsx(
                    'bg-bg-secondary shadow-2xl flex flex-col',
                    'animate-in fade-in duration-200',
                    // Mobilde: tam ekran, alttan açılma
                    fullScreenMobile ? [
                        'w-full h-[100dvh] sm:h-auto',
                        'rounded-none sm:rounded-2xl',
                        'slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:zoom-in-95',
                    ] : [
                        'w-[calc(100vw-1rem)] sm:w-auto',
                        'max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]',
                        'rounded-xl sm:rounded-2xl',
                        'zoom-in-95',
                    ],
                    // Desktop boyutları
                    'sm:w-full',
                    sizeClasses[size],
                    'sm:max-h-[90vh]'
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header - sticky */}
                <div
                    className={clsx(
                        'flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4',
                        'border-b border-border-light shrink-0 bg-bg-secondary',
                        // Mobilde safe area için üst padding
                        fullScreenMobile && 'pt-[max(0.75rem,env(safe-area-inset-top))]'
                    )}
                >
                    <h2 className="text-lg sm:text-xl font-bold text-text-primary pr-2 line-clamp-1">{title}</h2>
                    {!hideCloseButton && (
                        <button
                            onClick={onClose}
                            className="p-2.5 sm:p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center shrink-0"
                            aria-label="Kapat"
                        >
                            <X size={22} />
                        </button>
                    )}
                </div>

                {/* Body - scrollable */}
                <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1 min-h-0 overscroll-contain">
                    {children}
                </div>

                {/* Footer - sticky at bottom */}
                {footer && (
                    <div
                        className={clsx(
                            'flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3',
                            'px-4 sm:px-6 py-3 sm:py-4',
                            'border-t border-border-light shrink-0 bg-bg-tertiary',
                            'rounded-b-none sm:rounded-b-2xl',
                            // Mobilde safe area için alt padding
                            fullScreenMobile && 'pb-[max(0.75rem,env(safe-area-inset-bottom))]'
                        )}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

